<template>
  <ContentfulSelect
    label="Feldtyp"
    :required="true"
    :options="ATTRIBUTE_TYPE_SELECTION_ITEMS"
    :value="selectedAttributeSelectionItem.value"
    @input="handleTypeSelection"
  />
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue"
import ContentfulSelect, {
  ContentfulOption,
} from "app/ui-primitives/forms/ContentfulSelect.vue"
import { CustomAttributeTypeEnum } from "suus-api"
import { CUSTOM_ATTRIBUTE_TYPE_ICONS } from "app/modules/custom-attributes/types"

type CustomAttributeTypeSelectionItem = Omit<ContentfulOption, "value"> & {
  value: CustomAttributeTypeEnum
}

export const ATTRIBUTE_TYPE_SELECTION_ITEMS: CustomAttributeTypeSelectionItem[] =
  [
    {
      text: "Ganze Zahl",
      value: "Integer",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Integer,
      description:
        "<p>Erstellt ein Feld, das nur ganze Zahlen akzeptiert.</p><i>Beispiele: -3 | 32</i>",
    },
    {
      text: "Kommazahl",
      value: "Float",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Float,
      description:
        "<p>Erstellt ein Feld, das Kommazahlen akzeptiert.</p><i>Beispiele: -0.2 | 0 | 1024.0</i>",
    },
    {
      text: "Text",
      value: "String",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.String,
      description:
        "<p>Erstellt ein <b>einzeiliges, unformatiertes</b> Textfeld.</p><i>Beispiele: 'Beliebiger Freitext ohne Formatierung'</i>",
    },
    {
      text: "Textblock",
      value: "Text",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Text,
      description:
        "<p>Erstellt ein <b>mehrzeiliges, formatierbares</b> Feld, das freie Texteingabe akzeptiert.</p><i>Beispiele: 'Text mit <u>Formatierung</u>!'</i>",
    },
    {
      text: "Checkbox",
      value: "Boolean",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Boolean,
      description:
        "Erstellt eine Auswahlfeld, das entweder angehakt oder nicht angehakt sein kann.",
    },
    {
      text: "Genaues Datum",
      value: "Date",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Date,
      description:
        "<p>Erstellt ein Feld, das ein genaues Datum akzeptiert.</p><i>Beispiel: 20.10.2024</i>",
    },
    {
      text: "Ungefähres Datum",
      value: "FuzzyDate",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.FuzzyDate,
      description:
        "<p>Erstellt ein Feld, das ein ungefähres Datum akzeptiert. </p><i>Beispiele: 22.11.1870 | Januar 2022 | WS 22/23 | 2011</i>",
    },
    {
      text: "Auswahl",
      value: "Select",
      icon: CUSTOM_ATTRIBUTE_TYPE_ICONS.Select,
      description:
        "<p>Erstellt ein Feld, das eine Auswahl von vordefinierten Werten akzeptiert.</p>",
    },
  ]

/**
 *  props: attributeType
 *  emits: update:attributeType
 *  props: selectionValues
 *  emits: update:selectionValues
 *
 *  VALIDATION OUTSIDE OF COMPONENT
 */

export default defineComponent({
  name: "CustomAttributeTypeSelection",
  components: { ContentfulSelect },
  props: {
    attributeType: {
      type: String as PropType<CustomAttributeTypeEnum>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const selectedAttributeSelectionItem = computed(() => {
      return ATTRIBUTE_TYPE_SELECTION_ITEMS.find(
        (item) => item.value === props.attributeType
      )
    })

    const handleTypeSelection = (selection: CustomAttributeTypeEnum) => {
      emit("update:attributeType", selection)
    }

    return {
      ATTRIBUTE_TYPE_SELECTION_ITEMS,
      selectedAttributeSelectionItem,
      handleTypeSelection,
    }
  },
})
</script>
